import { useCallback } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  useGenerateAiMessage,
  useGenerateAiReply,
} from 'hooks/ai-messages-hooks';

/**
 * Interface for file review item
 */
export interface FileToReview {
  fileId: string;
  fileName: string;
  uploadedBy: string;
  fileSize: number;
  updatedAt: string; // ISO format date string
  contentType: string;
}

/**
 * Interface for AI functions returned by the hook
 */
interface AiFunctions {
  /**
   * Function to generate AI content based on input text
   * Returns a Promise that resolves to the generated content
   */
  generateAiMessageContent:
    | ((
        inputText: string,
      ) => Promise<{ content: string; subject: string; draftUuid: string | null }>)
    | undefined;

  /**
   * Function to generate AI reply based on thread conversation history
   * Returns a Promise that resolves to the generated content
   */
  generateAiReply:
    | ((
        request: { thread_id: string; message: string },
      ) => Promise<{ content: string; draftUuid: string | null }>)
    | undefined;

}

/**
 * Hook that provides AI-related functions based on feature flag status
 * @returns Object containing AI functions that may be defined or undefined based on feature flags
 */
export const useAiFunctions = (): AiFunctions => {
  const flags = useFlags();
  const isAiComposeButtonEnabled = flags?.enableAi === true;

  // Use the AI message generation mutation
  const generateAiMessageMutation = useGenerateAiMessage();
  const generateAiReplyMutation = useGenerateAiReply();
  // Define the AI content generation function only if the feature flag is enabled
  const generateAiMessageContent = useCallback(
    (
      inputText: string,
    ): Promise<{ content: string; subject: string; draftUuid: string | null }> =>
      new Promise((resolve, reject) => {
        generateAiMessageMutation.mutate(
          { data: { prompt: inputText } },
          {
            onSuccess: (response) => {
              // Handle successful generation
              if (response.success && response.draft) {
                resolve({
                  content: response.draft,
                  subject: response.subject_line,
                  draftUuid: response.ai_message_draft_uuid ?? null,
                });
              } else {
                const errorMessage =
                  'No content was generated. Please try again.';
                reject(new Error(errorMessage));
              }
            },
            onError: (error) => {
              // Check if it's a guardrail error
              if (error?.data?.error === 'guardrail_triggered') {
                const guardrailMessage =
                  error?.data?.error_explanation ||
                  'AI content generation blocked due to security concerns.';
                reject(new Error(guardrailMessage));
                return;
              }

              // Try different error structures
              const errorMessage =
                error?.data?.error_explanation ||
                error?.response?.data?.error_explanation ||
                error?.message ||
                'An error occurred while generating content.';
              reject(new Error(errorMessage));
            },
          },
        );
      }),
    [generateAiMessageMutation],
  );

  // Define the AI reply generation function only if the feature flag is enabled
  const generateAiReply = useCallback(
    (
      request: { thread_id: string; message: string },
    ): Promise<{ content: string; draftUuid: string | null }> =>
      new Promise((resolve, reject) => {
        generateAiReplyMutation.mutate(
          { data: request },
          {
            onSuccess: (response) => {
              // Handle successful generation
              if (response.success && response.draft) {
                resolve({
                  content: response.draft,
                  draftUuid: response.ai_message_draft_uuid ?? null,
                });
              } else {
                const errorMessage =
                  'No reply was generated. Please try again.';
                reject(new Error(errorMessage));
              }
            },
            onError: (error) => {
              // Check if it's a guardrail error
              if (error?.data?.error === 'guardrail_triggered') {
                const guardrailMessage =
                  error?.data?.error_explanation ||
                  'AI content generation blocked due to security concerns.';
                reject(new Error(guardrailMessage));
                return;
              }

              // Try different error structures
              const errorMessage =
                error?.data?.error_explanation ||
                error?.response?.data?.error_explanation ||
                error?.message ||
                'An error occurred while generating reply.';
              reject(new Error(errorMessage));
            },
          },
        );
      }),
    [generateAiReplyMutation],
  );

  return {
    generateAiMessageContent: isAiComposeButtonEnabled
      ? generateAiMessageContent
      : undefined,
    generateAiReply: isAiComposeButtonEnabled ? generateAiReply : undefined,
  };
};

export default useAiFunctions;
